/**
 * Native header search suggestions.
 *
 * Renders the predictive-search section plus the collection/page/query
 * suggestions under the header search input. Replaces the third-party
 * instant-search dropdown that previously bound to this field.
 *
 * Listeners are delegated from `document` rather than bound to the input:
 * the header subtree is re-rendered after page load, which silently discards
 * listeners attached directly to those nodes.
 */
(function () {
  var INPUT_ID = "search-dropdown-input";
  var RESULTS_ID = "search-suggestions";
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;

  var timer = null;
  var controller = null;
  var lastTerm = "";

  function getInput() {
    return document.getElementById(INPUT_ID);
  }

  function getResults() {
    return document.getElementById(RESULTS_ID);
  }

  function close() {
    var results = getResults();
    var input = getInput();
    if (results) {
      results.hidden = true;
      results.innerHTML = "";
    }
    if (input) input.setAttribute("aria-expanded", "false");
    document.body.classList.remove("search-suggestions-open");
  }

  function open(html) {
    var results = getResults();
    var input = getInput();
    if (!results) return;
    results.innerHTML = html;
    results.hidden = false;
    if (input) input.setAttribute("aria-expanded", "true");
    document.body.classList.add("search-suggestions-open");
  }

  function fetchSuggestions(term) {
    var results = getResults();
    if (!results) return;

    var suggestUrl = results.dataset.suggestUrl || "/search/suggest";
    var sectionId = results.dataset.sectionId || "predictive-search";

    if (controller) controller.abort();
    controller = typeof AbortController === "function" ? new AbortController() : null;
    var signal = controller ? { signal: controller.signal } : undefined;

    // resources[limit] is divided across the requested types, so products are
    // fetched on their own and the side columns come from the JSON API.
    var productsUrl =
      suggestUrl +
      "?q=" + encodeURIComponent(term) +
      "&section_id=" + encodeURIComponent(sectionId) +
      "&resources[type]=product&resources[limit]=6";

    // The JSON API ships full page bodies (~549 KB a query) for fields we never
    // read, so the side columns come from a second render of the same section.
    var sideUrl =
      suggestUrl +
      "?q=" + encodeURIComponent(term) +
      "&section_id=" + encodeURIComponent(sectionId) +
      "&resources[type]=collection,article,page,query&resources[limit]=8";

    Promise.all([
      fetch(productsUrl, signal).then(function (r) {
        if (!r.ok) throw new Error("Suggestions failed: " + r.status);
        return r.text();
      }),
      fetch(sideUrl, signal)
        .then(function (r) {
          return r.ok ? r.text() : "";
        })
        .catch(function () {
          return "";
        })
    ])
      .then(function (parts) {
        var input = getInput();
        if (!input || input.value.trim() !== term) return;

        var productsHtml = parts[0] || "";
        var sideHtml = parts[1] || "";

        var hasProducts = productsHtml.indexOf("ps-product") !== -1;
        var hasSide = sideHtml.indexOf("ps-col--side") !== -1;
        if (!hasProducts && !hasSide) {
          open('<div class="ps-empty p2 tc">No results found.</div>');
          return;
        }

        open('<div class="ps-cols">' + sideHtml + productsHtml + "</div>");
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") return;
        close();
      });
  }

  // The header's Vue app does not mount outside the homepage (another app owns
  // the window.Vue global), so the search toggle's Vue binding never fires and
  // the field is unreachable there. Toggle the form directly instead; adding a
  // class that is already present is a no-op where Vue does work.
  document.addEventListener("click", function (event) {
    var opener = event.target.closest ? event.target.closest("#search-open") : null;
    var closer = event.target.closest ? event.target.closest("#search-close") : null;
    if (!opener && !closer) return;

    var form = document.getElementById("search-form");
    if (!form) return;

    if (opener) {
      form.classList.add("show");
      var input = getInput();
      if (input) {
        try {
          input.focus({ focusVisible: true });
        } catch (e) {
          input.focus();
        }
      }
    } else {
      form.classList.remove("show");
      close();
    }
  });

  document.addEventListener("input", function (event) {
    var target = event.target;
    if (!target || target.id !== INPUT_ID) return;

    var term = target.value.trim();
    if (timer) clearTimeout(timer);

    if (term.length < MIN_CHARS) {
      lastTerm = "";
      close();
      return;
    }
    if (term === lastTerm && !getResults().hidden) return;
    lastTerm = term;

    timer = setTimeout(function () {
      fetchSuggestions(term);
    }, DEBOUNCE_MS);
  });

  document.addEventListener("focusin", function (event) {
    var target = event.target;
    if (!target || target.id !== INPUT_ID) return;
    var term = target.value.trim();
    if (term.length >= MIN_CHARS) fetchSuggestions(term);
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    var results = getResults();
    if (!results || results.hidden) return;
    close();
    var input = getInput();
    if (input) input.focus();
  });

  document.addEventListener("click", function (event) {
    var results = getResults();
    if (!results || results.hidden) return;
    var input = getInput();
    if (results.contains(event.target)) return;
    if (input && input.contains(event.target)) return;
    close();
  });
})();
